import React, { useState } from "react";

import { faPlus } from "@fortawesome/pro-regular-svg-icons";

import { ButtonStandard, InputWrapper, SingleSelectInput } from "@performyard/py-components";

type CompetencyLite = {
	_id: string;
	name: string;
	levels: Array<{ rank: number; name: string }>;
};

export type AddCompetencyPickerProps = {
	availableCompetencies: CompetencyLite[];
	onAdd: (entry: { competency_id: string; expected_level: number | null }) => void;
	disabled?: boolean;
};

export const AddCompetencyPicker: React.FC<AddCompetencyPickerProps> = ({ availableCompetencies, onAdd, disabled }) => {
	const [selectedCompetencyId, setSelectedCompetencyId] = useState<string | null>(null);
	const [selectedLevel, setSelectedLevel] = useState<number | null>(null);
	const [showCompetencyError, setShowCompetencyError] = useState(false);

	const competencyOptions = availableCompetencies.map((c) => ({ value: c._id, label: c.name }));
	const selectedCompetency = availableCompetencies.find((c) => c._id === selectedCompetencyId);
	const levelOptions = selectedCompetency?.levels.map((lvl) => ({ value: lvl.rank, label: lvl.name })) ?? [];
	const hasLevels = levelOptions.length > 0;

	const handleCompetencyChange = (next: string | null | undefined) => {
		setSelectedCompetencyId(next ?? null);
		setSelectedLevel(null);
		if (next) setShowCompetencyError(false);
	};

	const handleAdd = () => {
		if (!selectedCompetencyId) {
			setShowCompetencyError(true);
			return;
		}
		onAdd({ competency_id: selectedCompetencyId, expected_level: selectedLevel });
		setSelectedCompetencyId(null);
		setSelectedLevel(null);
		setShowCompetencyError(false);
	};

	return (
		// Grid with the same column tracks as the bottom button row and entry rows. No padding —
		// every layer (picker, rows, buttons) spans the full form-content width.
		<div className="grid grid-cols-[1fr_192px_76px] items-start gap-3" data-testid="competency-picker">
			<InputWrapper
				id="picker-competency"
				label="Competency"
				error={showCompetencyError ? "Please select a competency" : undefined}
			>
				<SingleSelectInput<string>
					id="picker-competency"
					value={selectedCompetencyId ?? undefined}
					placeholder="Select a competency"
					options={competencyOptions}
					onChange={handleCompetencyChange}
					disabled={disabled || competencyOptions.length === 0}
					errored={showCompetencyError}
				/>
			</InputWrapper>
			<InputWrapper id="picker-level" label="Expected Level">
				<SingleSelectInput<number>
					id="picker-level"
					value={selectedLevel ?? undefined}
					placeholder={
						!selectedCompetency ? "Make a selection" : hasLevels ? "Make a selection" : "No levels created"
					}
					options={levelOptions}
					onChange={(v) => setSelectedLevel(v ?? null)}
					disabled={!selectedCompetency || levelOptions.length === 0}
					isClearable
				/>
			</InputWrapper>
			{/* Invisible label spacer matches InputWrapper's label+gap height so the Add button
			    aligns vertically with the input row. */}
			<div className="flex flex-col gap-2">
				<span aria-hidden className="invisible text-paragraph-3">
					&nbsp;
				</span>
				<ButtonStandard
					variant="solid"
					color="primary"
					size="medium"
					iconLeft={faPlus}
					disabled={disabled}
					onClick={handleAdd}
					data-testid="add-competency-button"
				>
					Add
				</ButtonStandard>
			</div>
		</div>
	);
};
